import { z } from "zod";
// ── Enums ─────────────────────────────────────────────
export const briefingStatusSchema = z.enum(["pending", "generating", "completed", "failed"]);
// ── Metadata ──────────────────────────────────────────
export const briefingMetadataSchema = z.object({
    model: z.string(),
    provider: z.string(),
    formattingModel: z.string(),
    analysisTokenUsage: z.object({
        input: z.number(),
        output: z.number(),
    }),
    formattingTokenUsage: z.object({
        input: z.number(),
        output: z.number(),
    }),
    generationDurationMs: z.number(),
    analysisDurationMs: z.number(),
});
// ── Briefing Prompt ───────────────────────────────────
export const briefingPromptSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    userId: z.uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    promptText: z.string(),
    webSummaryPrompt: z.string().nullable(),
    webReportPrompt: z.string().nullable(),
    slackSummaryPrompt: z.string().nullable(),
    slackReportPrompt: z.string().nullable(),
    isActive: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const createBriefingPromptInputSchema = z.object({
    name: z
        .string()
        .check(z.minLength(1, "Name is required"), z.maxLength(200, "Name must be under 200 characters")),
    description: z.string().check(z.maxLength(1000)).optional(),
    promptText: z.string().check(z.minLength(1, "Prompt text is required")),
    webSummaryPrompt: z.string().optional(),
    webReportPrompt: z.string().optional(),
    slackSummaryPrompt: z.string().optional(),
    slackReportPrompt: z.string().optional(),
});
export const updateBriefingPromptInputSchema = z.object({
    name: z.string().check(z.minLength(1), z.maxLength(200)).optional(),
    description: z.string().check(z.maxLength(1000)).nullable().optional(),
    promptText: z.string().check(z.minLength(1)).optional(),
    webSummaryPrompt: z.string().nullable().optional(),
    webReportPrompt: z.string().nullable().optional(),
    slackSummaryPrompt: z.string().nullable().optional(),
    slackReportPrompt: z.string().nullable().optional(),
    isActive: z.boolean().optional(),
});
// ── Briefing ──────────────────────────────────────────
export const briefingSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    promptId: z.uuid().nullable(),
    userId: z.uuid().nullable(),
    slug: z.string(),
    title: z.string(),
    status: briefingStatusSchema,
    deliverToSlack: z.boolean(),
    analysisOutput: z.string().nullable(),
    webSummary: z.string().nullable(),
    slackSummary: z.string().nullable(),
    webReport: z.string().nullable(),
    slackReport: z.string().nullable(),
    metadata: briefingMetadataSchema.nullable(),
    error: z.string().nullable(),
    threadId: z.string().nullable(),
    generatedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const generateBriefingInputSchema = z.object({
    promptId: z.uuid(),
    deliverToSlack: z.boolean().optional().default(false),
});
// ── Responses ─────────────────────────────────────────
export const briefingPromptListResponseSchema = z.object({
    prompts: z.array(briefingPromptSchema),
});
export const briefingListResponseSchema = z.object({
    briefings: z.array(briefingSchema),
});
// ── Briefing Document (public — omit storagePath, storageType, fileHash) ──
export const briefingDocumentSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    userId: z.uuid().nullable(),
    title: z.string(),
    originalFileName: z.string(),
    fileSizeBytes: z.number().int(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const briefingDocumentListResponseSchema = z.object({
    documents: z.array(briefingDocumentSchema),
});
export const uploadBriefingDocumentInputSchema = z.object({
    title: z
        .string()
        .check(z.minLength(1, "Title is required"), z.maxLength(200, "Title must be under 200 characters")),
});
